/**
 * @file The ArTables the open form version holds, by `tableId` in layout order
 * (SYD-2450). Consumer: `useFormRecoveryList`, which lists one restore point
 * ladder per id and merges them with the version's into one recovery timeline.
 *
 * The authority is the form type's layout — the same `defaultUILayout.structure`
 * `FormUI` renders from — walked by `discoverArTablesInLayoutStructure`, which
 * already visits every container and dedupes by id.
 *
 * NOT `formStore.state.tables`. That `proxyMap` is filled by `ArTableShell` as
 * each table MOUNTS, so it holds the tables currently rendered: a table in a
 * collapsed section, below the fold, or hidden by the active visibility view is
 * absent from it. A recovery timeline built from that map would tell the operator
 * a table did not change when it did. Visibility is left out for the same reason —
 * a table the current view hides still has rows, and those rows still changed.
 */

import { useMemo } from 'react';
import { useSnapshot } from 'valtio';

import type { ArTableId } from '#types';
import type { UIElement } from '#types/System.types';
import discoverArTablesInLayoutStructure from '@/components/modals/FormEmailModal/create/discoverArTablesInLayoutStructure';
import { formStore } from '@/store';

export const useFormTableIds = (): ArTableId[] => {
  const formTypeSnap = useSnapshot(formStore.state.formType);
  const layoutStructure = formTypeSnap.data?.defaultUILayout?.structure as UIElement | undefined;

  return useMemo(
    () =>
      layoutStructure === undefined
        ? []
        : discoverArTablesInLayoutStructure(layoutStructure).map((table) => table.tableId),
    [layoutStructure],
  );
};
